import React, { useCallback, useEffect, useState } from 'react';
import {
  getInternalWalletB2BPing,
  isInternalWalletApiConfigured,
  postInternalWalletCreditLine,
  postInternalWalletRepayment,
} from '../../services/internalWalletService';
import { useToast } from '../../context/ToastContext';
import { getInternalErrorMessage } from '../../services/internalApi';

/**
 * Parse INR (rupees) from user input into minor units (paise) for the API.
 * @param {string} raw
 * @param {{ allowZero: boolean }} opts - repayment uses allowZero: false; credit line uses true
 */
function parseInrMajorToMinor(raw, { allowZero }) {
  let s = String(raw).trim().replace(/,/g, '');
  if (s === '') return { ok: false, message: 'Enter an amount in INR.' };
  if (/^\.\d/.test(s)) s = `0${s}`;
  if (!/^\d+(\.\d{0,2})?$/.test(s)) {
    return { ok: false, message: 'Enter a valid INR amount (up to two decimal places).' };
  }
  const dot = s.indexOf('.');
  const intPart = dot === -1 ? s : s.slice(0, dot);
  const frac = dot === -1 ? '00' : `${s.slice(dot + 1)}00`.slice(0, 2);
  const minor = Number(intPart) * 100 + Number(frac);
  if (!Number.isSafeInteger(minor) || minor < 0) {
    return { ok: false, message: 'Amount is too large or invalid.' };
  }
  if (!allowZero && minor < 1) {
    return { ok: false, message: 'Amount must be greater than zero.' };
  }
  return { ok: true, value: minor };
}

/**
 * When `REACT_APP_INTERNAL_API_BASE_URL` is set and GET …/internal/wallet/ping succeeds,
 * shows internal B2B actions allowed by the API (`features.wallet_repayment`, `features.wallet_credit_line`,
 * `features.wallet_credit_issue_requests` is surfaced on the ping payload for other screens).
 * Copy stays generic; operators use the same controls for any wallet currency.
 */
function InternalB2BWalletOps({ walletId, onOperationSuccess, onPingResult }) {
  const toast = useToast();
  /** @type {['idle'|'loading'|'ready'|'unreachable', any]} */
  const [pingStatus, setPingStatus] = useState('idle');
  const [pingPayload, setPingPayload] = useState(null);

  const [repayOpen, setRepayOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [repaySubmitting, setRepaySubmitting] = useState(false);
  const [creditSubmitting, setCreditSubmitting] = useState(false);

  const [amountInr, setAmountInr] = useState('');
  const [rrn, setRrn] = useState('');
  const [repaymentDateLocal, setRepaymentDateLocal] = useState('');
  const [repayNotes, setRepayNotes] = useState('');

  const [creditLimitInr, setCreditLimitInr] = useState('');
  const [creditNotes, setCreditNotes] = useState('');

  const runPing = useCallback(async () => {
    if (!isInternalWalletApiConfigured()) {
      setPingStatus('idle');
      setPingPayload(null);
      return;
    }
    setPingStatus('loading');
    setPingPayload(null);
    try {
      const data = await getInternalWalletB2BPing();
      setPingPayload(data && typeof data === 'object' ? data : {});
      setPingStatus('ready');
    } catch (e) {
      console.error(e);
      setPingStatus('unreachable');
      setPingPayload(null);
    }
  }, []);

  useEffect(() => {
    runPing();
  }, [runPing]);

  useEffect(() => {
    onPingResult?.(pingStatus, pingPayload);
  }, [pingStatus, pingPayload, onPingResult]);

  useEffect(() => {
    setRepayOpen(false);
    setCreditOpen(false);
    setAmountInr('');
    setRrn('');
    setRepaymentDateLocal('');
    setRepayNotes('');
    setCreditLimitInr('');
    setCreditNotes('');
  }, [walletId]);

  const features = pingPayload?.features;
  const reachable = pingPayload?.accessible !== false;
  const showRepay = pingStatus === 'ready' && reachable && features?.wallet_repayment === true;
  const showCreditLine = pingStatus === 'ready' && reachable && features?.wallet_credit_line === true;

  if (!isInternalWalletApiConfigured()) {
    return null;
  }

  const closeRepay = () => {
    if (!repaySubmitting) setRepayOpen(false);
  };
  const closeCredit = () => {
    if (!creditSubmitting) setCreditOpen(false);
  };

  const submitRepayment = async (e) => {
    e.preventDefault();
    if (!walletId?.trim()) return;
    const amt = parseInrMajorToMinor(amountInr, { allowZero: false });
    if (!amt.ok) {
      toast.error(amt.message);
      return;
    }
    const r = rrn.trim();
    if (!r) {
      toast.error('RRN is required.');
      return;
    }
    let repayment_date;
    if (repaymentDateLocal.trim()) {
      const ms = new Date(repaymentDateLocal).getTime();
      if (!Number.isFinite(ms)) {
        toast.error('Repayment date is invalid.');
        return;
      }
      repayment_date = ms;
    }
    const notes = repayNotes.trim();
    setRepaySubmitting(true);
    try {
      const body = {
        amount_minor: amt.value,
        rrn: r,
        ...(repayment_date !== undefined ? { repayment_date } : {}),
        ...(notes ? { notes } : {}),
      };
      const res = await postInternalWalletRepayment(walletId.trim(), body);
      if (res && res.success === true) {
        toast.success('Repayment recorded.');
        closeRepay();
        onOperationSuccess?.();
      } else {
        toast.error(typeof res?.message === 'string' ? res.message : 'Repayment request did not succeed.');
      }
    } catch (err) {
      console.error(err);
      toast.error(getInternalErrorMessage(err, 'Repayment failed'));
    } finally {
      setRepaySubmitting(false);
    }
  };

  const submitCreditLine = async (e) => {
    e.preventDefault();
    if (!walletId?.trim()) return;
    const lim = parseInrMajorToMinor(creditLimitInr, { allowZero: true });
    if (!lim.ok) {
      toast.error(lim.message);
      return;
    }
    const notes = creditNotes.trim();
    setCreditSubmitting(true);
    try {
      const body = {
        credit_line_limit_minor: lim.value,
        ...(notes ? { notes } : {}),
      };
      const res = await postInternalWalletCreditLine(walletId.trim(), body);
      if (res && res.success === true) {
        toast.success('Credit line updated.');
        closeCredit();
        onOperationSuccess?.();
      } else {
        toast.error(typeof res?.message === 'string' ? res.message : 'Credit line request did not succeed.');
      }
    } catch (err) {
      console.error(err);
      toast.error(getInternalErrorMessage(err, 'Credit line update failed'));
    } finally {
      setCreditSubmitting(false);
    }
  };

  if (pingStatus === 'loading' || pingStatus === 'idle') {
    return null;
  }
  if (pingStatus === 'unreachable' || (!showRepay && !showCreditLine)) {
    return null;
  }

  return (
    <>
      {showRepay ? (
        <button
          type="button"
          onClick={() => setRepayOpen(true)}
          className="btn-secondary inline-flex items-center gap-1.5 border-amber-200 bg-amber-50 py-1.5 pl-2.5 pr-3 text-sm text-amber-900 hover:bg-amber-100"
          title="Record repayment"
        >
          <i className="material-icons-outlined text-base">payments</i>
          Repay
        </button>
      ) : null}
      {showCreditLine ? (
        <button
          type="button"
          onClick={() => setCreditOpen(true)}
          className="btn-secondary inline-flex items-center gap-1.5 border-sky-200 bg-sky-50 py-1.5 pl-2.5 pr-3 text-sm text-sky-900 hover:bg-sky-100"
          title="Set credit line"
        >
          <i className="material-icons-outlined text-base">credit_score</i>
          Credit line
        </button>
      ) : null}

      {repayOpen ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) closeRepay();
          }}
        >
          <div
            className="card-surface max-h-[90vh] w-full max-w-md overflow-y-auto p-6 shadow-xl"
            role="dialog"
            aria-labelledby="b2b-repay-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2 id="b2b-repay-title" className="text-lg font-semibold text-slate-900">
              Record repayment
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Record a repayment for wallet <span className="font-mono text-slate-800">{walletId}</span>.
            </p>
            <form className="mt-4 flex flex-col gap-3" onSubmit={submitRepayment}>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Amount (INR)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amountInr}
                  onChange={(e) => setAmountInr(e.target.value)}
                  placeholder="e.g. 10000.50"
                  className="rounded-lg border border-slate-200 px-3 py-2 font-mono text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">RRN</span>
                <input
                  type="text"
                  value={rrn}
                  onChange={(e) => setRrn(e.target.value)}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Repayment date and time (optional)</span>
                <input
                  type="datetime-local"
                  value={repaymentDateLocal}
                  onChange={(e) => setRepaymentDateLocal(e.target.value)}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Notes (optional)</span>
                <textarea
                  value={repayNotes}
                  onChange={(e) => setRepayNotes(e.target.value)}
                  rows={2}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <div className="mt-2 flex flex-wrap justify-end gap-2">
                <button type="button" className="btn-secondary" disabled={repaySubmitting} onClick={closeRepay}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={repaySubmitting}>
                  {repaySubmitting ? 'Submitting…' : 'Submit repayment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {creditOpen ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) closeCredit();
          }}
        >
          <div
            className="card-surface max-h-[90vh] w-full max-w-md overflow-y-auto p-6 shadow-xl"
            role="dialog"
            aria-labelledby="b2b-credit-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2 id="b2b-credit-title" className="text-lg font-semibold text-slate-900">
              Set credit line
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Set the credit line limit for wallet <span className="font-mono text-slate-800">{walletId}</span>.
            </p>
            <form className="mt-4 flex flex-col gap-3" onSubmit={submitCreditLine}>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Credit line limit (INR)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={creditLimitInr}
                  onChange={(e) => setCreditLimitInr(e.target.value)}
                  placeholder="e.g. 50000.00"
                  className="rounded-lg border border-slate-200 px-3 py-2 font-mono text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                  required
                />
                <span className="text-xs text-slate-500">In Rupees; Enter 0 to remove the credit line.</span>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Notes (optional)</span>
                <textarea
                  value={creditNotes}
                  onChange={(e) => setCreditNotes(e.target.value)}
                  rows={2}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <div className="mt-2 flex flex-wrap justify-end gap-2">
                <button type="button" className="btn-secondary" disabled={creditSubmitting} onClick={closeCredit}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={creditSubmitting}>
                  {creditSubmitting ? 'Submitting…' : 'Update credit line'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default InternalB2BWalletOps;
